import { supabase } from "@/integrations/supabase/client";

// Google Analytics Tracking ID
const GA_TRACKING_ID = "G-QEFPP8PV4H";

export const CONSENT_KEY = "cookie-consent";
export const VISITOR_ID_KEY = "visitor-id";

/**
 * Bump this when the policy or available cookie categories change.
 * Any stored consent with a different version is wiped and the user
 * is re-prompted.
 */
export const CONSENT_VERSION = 2;

/**
 * Consent expires after 12 months (EDPB / IMY guidance).
 */
export const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  advertising: boolean;
}

export interface StoredConsent {
  preferences: CookiePreferences;
  timestamp: number;
  version: number;
}

/**
 * Loads the Google Analytics script
 */
const loadGoogleAnalyticsScript = () => {
  if (!GA_TRACKING_ID) {
    console.warn("Google Analytics Tracking ID not configured");
    return;
  }

  if (document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)) {
    return;
  }

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag(..._: any[]): void {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  }
  (window as any).gtag = gtag;

  gtag("js", new Date());
  gtag("config", GA_TRACKING_ID);
};

/**
 * Initialize Google Consent Mode v2
 */
export const initializeConsent = () => {
  window.dataLayer = window.dataLayer || [];
  function gtag(..._: any[]): void {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  }
  (window as any).gtag = gtag;

  gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });

  loadGoogleAnalyticsScript();
};

/**
 * Update consent when user makes a choice
 */
export const updateConsent = (preferences: CookiePreferences) => {
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: preferences.analytics ? "granted" : "denied",
      ad_storage: preferences.advertising ? "granted" : "denied",
      ad_user_data: preferences.advertising ? "granted" : "denied",
      ad_personalization: preferences.advertising ? "granted" : "denied",
    });
  }
};

/**
 * Read stored consent. Returns null (and clears storage) if missing,
 * malformed, from a previous policy version, or older than 12 months.
 */
export const getStoredConsent = (): StoredConsent | null => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);

    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof parsed.version !== "number" ||
      typeof parsed.timestamp !== "number" ||
      typeof parsed.preferences !== "object"
    ) {
      localStorage.removeItem(CONSENT_KEY);
      return null;
    }

    if (parsed.version !== CONSENT_VERSION) {
      localStorage.removeItem(CONSENT_KEY);
      return null;
    }

    if (Date.now() - parsed.timestamp > CONSENT_MAX_AGE_MS) {
      localStorage.removeItem(CONSENT_KEY);
      return null;
    }

    return parsed as StoredConsent;
  } catch {
    localStorage.removeItem(CONSENT_KEY);
    return null;
  }
};

/**
 * Persist consent with timestamp + version metadata.
 */
export const saveConsent = (preferences: CookiePreferences): StoredConsent => {
  const record: StoredConsent = {
    preferences,
    timestamp: Date.now(),
    version: CONSENT_VERSION,
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  return record;
};

/**
 * Stable anonymous visitor id used to deduplicate consent log entries.
 * Not linked to any personal data.
 */
const getVisitorId = (): string => {
  let id = localStorage.getItem(VISITOR_ID_KEY);
  if (!id) {
    id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(VISITOR_ID_KEY, id);
  }
  return id;
};

/**
 * Fire-and-forget audit log of a consent decision. Silently swallows
 * errors so it never blocks the UI.
 */
export const logConsentToBackend = async (
  choice: "accept_all" | "reject_non_essential" | "custom",
  preferences: CookiePreferences,
) => {
  try {
    await supabase.from("consent_log").insert({
      visitor_id: getVisitorId(),
      choice,
      analytics: preferences.analytics,
      advertising: preferences.advertising,
      policy_version: CONSENT_VERSION,
      user_agent: navigator.userAgent,
      page_url: window.location.href,
    });
  } catch {
    // best-effort
  }
};

/**
 * Check if user has given consent for analytics cookies
 */
export const hasAnalyticsConsent = (): boolean => {
  return getStoredConsent()?.preferences.analytics === true;
};

/**
 * Get current consent preferences (or null if not yet set / expired)
 */
export const getConsentPreferences = (): CookiePreferences | null => {
  return getStoredConsent()?.preferences ?? null;
};

/**
 * Clear consent (useful for testing or allowing user to reset)
 */
export const clearConsent = () => {
  localStorage.removeItem(CONSENT_KEY);
};

/* ------------------------------------------------------------------ */
/*  Custom event tracking                                              */
/* ------------------------------------------------------------------ */

export const trackEvent = (
  eventName: string,
  params: Record<string, string | number | boolean | undefined> = {},
) => {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined),
  );

  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", eventName, clean);
  }
};

declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
  }
}
